import { useEffect } from 'react';
import ReactGA from 'react-ga';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import { CircleLoader, TextError } from 'src/components';
import { Screen } from 'src/components/Screen';
import { SCRoutes } from 'src/config';
import { SiteHeader } from 'src/containers/SiteHeader';
import { SocketWrapper } from 'src/containers/SocketWrapper';
import { appConfigStateSelector, startLoadAppSettings } from 'src/features/appSettings';
import { LoginScreen } from 'src/features/auth/LoginScreen';
import { isAuthenticatedSelector } from 'src/features/auth/store';
import { EStoreHome } from 'src/features/client';
import { HomeToStoreLandingPage } from 'src/features/client/HomeToStoreLandingPage';
import { HomToStoreContainer } from 'src/features/client/HomToStoreContainer';
import { InStoreHome } from 'src/features/client/InStore';
import { Dashboard } from 'src/features/dashboard/Dashboard';
import { DeliveryPublicColiInfo } from 'src/features/DeliveryPublicColiInfo';
import { useSubscriptionAlert } from 'src/features/ocm/useSubscriptionAlert';
import { PageNotFound } from 'src/features/PageNotFound';
import { PrinterPageContainer } from 'src/features/printer';
import { ClientDeliveryCustomerConfirmationContainer } from 'src/features/PublicClientDeliveryCustomerConfirmation';
import { PublicClientDeliveryRequest } from 'src/features/PublicClientDeliveryRequest';
import { PublicClientDeliveryRequestClientRatingContainer } from 'src/features/PublicClientDeliveryRequestClientRating/PublicClientDeliveryRequestClientRatingContainer';
import { PublicClientDeliveryRequestPaymentCanceled } from 'src/features/PublicClientDeliveryRequestPaymentCanceled';
import { PublicClientDeliveryRequestPaymentSuccess } from 'src/features/PublicClientDeliveryRequestPaymentSuccess';
import { PublicClientDeliveryTrackingContainer } from 'src/features/PublicClientDeliveryTracking';
import { ResetPassword } from 'src/features/ResetPassword';
import { TrackingColisPublic } from 'src/features/TrackingColisPublic';
import { isAgentUserSelector, isSuperAdminUserSelector } from 'src/features/user/store';
import { useOCM, useSwitchToBrowserLanguageIfSupported } from 'src/hooks';
import { useUserRole } from 'src/hooks/useUserRole';

export const MainApp = (): JSX.Element => {
  useOCM();
  useSwitchToBrowserLanguageIfSupported();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { isLoading, error } = useSelector(appConfigStateSelector);

  const history = useHistory();
  const isAgent = useSelector(isAgentUserSelector);
  const isSupperAdmin = useSelector(isSuperAdminUserSelector);

  const { level } = useSubscriptionAlert();

  const { LEVEL_MANAGER } = useUserRole();
  const regularAgent = (isAgent || isSupperAdmin) && level !== 'ALERT';

  const dispatch = useDispatch();

  useEffect(() => {
    // This huck is to redirect the user to the login page if he is not authenticated
    // Only for dashboard
    if (!isAuthenticated && history.location.pathname.includes('dashboard')) {
      history.push('/');
    }
  }, [isAuthenticated, history]);

  useEffect(() => {
    const currentPAge = history.location.pathname ?? '/';
    ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_UA ?? 'UA-217132864-2');
    ReactGA.pageview(`${currentPAge}${history.location.search}`);

    return history.listen((location) => {
      ReactGA.pageview(`${location.pathname}${location.search}`);
    });
  }, [history]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(startLoadAppSettings());
    }
  }, [isAuthenticated, dispatch]);

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route component={ResetPassword} path={SCRoutes.resetPassword} exact={true} />
        <Route component={LoginScreen} path="/" exact={true} />
        <Route component={TrackingColisPublic} path={SCRoutes.trackingColis} exact={true} />
        <Route component={DeliveryPublicColiInfo} path={SCRoutes.deliveryColisInfo} exact={true} />
        <Route component={PublicClientDeliveryTrackingContainer} path={SCRoutes.requestDeliveryTracking} exact={true} />
        <Route
          component={PublicClientDeliveryRequestClientRatingContainer}
          path={SCRoutes.clientDeliveryRating}
          exact={true}
        />
        <Route
          component={ClientDeliveryCustomerConfirmationContainer}
          path={SCRoutes.clientDeliveryCustomerConfirmation}
          exact={true}
        />
        <Route
          component={PublicClientDeliveryRequestPaymentSuccess}
          path={SCRoutes.clientDeliveryCustomerStripPaymentSuccess}
          exact={true}
        />

        <Route
          component={PublicClientDeliveryRequestPaymentCanceled}
          path={SCRoutes.clientDeliveryCustomerStripPaymentCanceled}
          exact={true}
        />
        <Route component={PublicClientDeliveryRequest} path={SCRoutes.requestDelivery} exact={true} />
        <Route component={EStoreHome} path={SCRoutes.eStoreRoute} />
        <Route component={InStoreHome} path={SCRoutes.InStoreRoute} />
        <Route component={HomToStoreContainer} path={SCRoutes.HomeToStoreRoute} />
        <Route component={HomeToStoreLandingPage} path={SCRoutes.HomeSendToStoreAgenciesListFromFrance} />
        <Route component={HomeToStoreLandingPage} path={SCRoutes.HomeSendToStoreAgenciesListFromMorocco} />
        <Route path="*" component={PageNotFound} exact={true} />
      </Switch>
    );
  }

  if (isLoading) {
    return (
      <Screen center>
        <CircleLoader />
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen center>
        <TextError message={error.message} />
      </Screen>
    );
  }

  return (
    <SocketWrapper>
      <div>
        <SiteHeader />
        <Switch>
          <Route component={ResetPassword} path={SCRoutes.resetPassword} exact={true} />
          {!!regularAgent && <Route component={PrinterPageContainer} path={'/'} exact={true} />}
          <Route component={TrackingColisPublic} path={SCRoutes.trackingColis} exact={true} />
          <Route component={DeliveryPublicColiInfo} path={SCRoutes.deliveryColisInfo} exact={true} />
          <Route component={EStoreHome} path={SCRoutes.eStoreRoute} />
          <Route component={InStoreHome} path={SCRoutes.InStoreRoute} />

          {LEVEL_MANAGER && <Route path={SCRoutes.dashboard.home} component={Dashboard} />}

          <Route path="*" component={PageNotFound} />
        </Switch>
      </div>
    </SocketWrapper>
  );
};
